import { useMemo, useState } from "react";
import "./FollowUpsPage.css";
import { useAuth } from "../../../hooks/useAuth";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { useFollowUps } from "../../../hooks/useFollowUps";
import { useClientLookup } from "../../../hooks/useClientLookup";
import { useBranches } from "../../../hooks/useBranches";
import { useServices } from "../../../hooks/useServices";
import { useUsers } from "../../../hooks/useUsers";
import {
  createFollowUpAsync,
  deleteFollowUpAsync,
  updateFollowUpAsync,
} from "../../../api/FollowUpApi";
import {
  CreateFollowUpRequest,
  FollowUp,
  FollowUpStatus,
  FollowUpUpdateModel,
} from "../../../models/FollowUpModels";
import { Constants } from "../../../constants/constants";
import { OrgForms } from "../../../config/forms";
import DateRangeFilter, {
  DateRangeValue,
  emptyDateRangeValue,
  resolveDateRange,
} from "../../../components/DateRangeFilter";
import FollowUpModal, {
  FollowUpFormValues,
  FollowUpModalMode,
} from "./FollowUpModal";
import ClientModal, { ClientFormValues } from "../clients/ClientModal";
import Toast from "../../../components/Toast";
import SubscriptionSectionGate from "../../../components/SubscriptionSectionGate";
import { formatDateTime } from "../../../utils/DateTime";

const FOLLOWUP_STATUS_LABELS: Record<FollowUpStatus, string> = {
  [FollowUpStatus.Pending]: "Pending",
  [FollowUpStatus.Contacted]: "Contacted",
  [FollowUpStatus.Completed]: "Completed",
  [FollowUpStatus.Cancelled]: "Cancelled",
  [FollowUpStatus.Skipped]: "Skipped",
};

const FOLLOWUP_STATUS_CLASS: Record<FollowUpStatus, string> = {
  [FollowUpStatus.Pending]: "pending",
  [FollowUpStatus.Contacted]: "contacted",
  [FollowUpStatus.Completed]: "completed",
  [FollowUpStatus.Cancelled]: "cancelled",
  [FollowUpStatus.Skipped]: "skipped",
};

const PRIORITY_LABELS: Record<number, string> = {
  1: "Low",
  2: "Medium",
  3: "High",
};

const toUpdateRequest = (
  form: FollowUpFormValues,
): FollowUpUpdateModel => ({
  branchId: form.branchId || undefined,
  title: form.title || undefined,
  notes: form.notes || undefined,
  dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
  status: Number(form.status) as FollowUpStatus,
  priority: Number(form.priority),
  assignedTo: form.assignedTo || undefined,
  assignedName: form.assignedName || undefined,
  lastActionNote: form.lastActionNote || undefined,
});

const isOverdue = (followUp: FollowUp): boolean =>
  followUp.status === FollowUpStatus.Pending &&
  new Date(followUp.dueAt).getTime() < Date.now();

interface IconProps {
  title?: string;
}

const EditIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const ContactIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const CompleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const DeleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const FollowUpsPage = () => {
  const { user, hasPrivilege } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;
  const canCreateFollowUps = hasPrivilege(OrgForms.FollowUps, "canCreate");
  const canEditFollowUps = hasPrivilege(OrgForms.FollowUps, "canEdit");
  const canDeleteFollowUps = hasPrivilege(OrgForms.FollowUps, "canDelete");
  const canCreateClients = hasPrivilege(OrgForms.Clients, "canCreate");
  const { entitlements, loading: entitlementsLoading } = useEntitlements();

  const {
    followUps,
    pagination,
    loading,
    error,
    filters,
    setFilters,
    setPage,
    reload,
  } = useFollowUps(organizationId);

  const clientPicker = useClientLookup(organizationId);
  const { branches } = useBranches(organizationId);
  const { services } = useServices(organizationId);
  const { users: staff } = useUsers(organizationId, 100);

  const staffMap = useMemo(
    () => new Map(staff.map((member) => [member.id, member])),
    [staff],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<FollowUpModalMode>("create");
  const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUp | null>(
    null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [range, setRange] = useState<DateRangeValue>(emptyDateRangeValue());
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const openModal = (mode: FollowUpModalMode, followUp: FollowUp | null) => {
    clientPicker.clearCreatedClient();
    setModalMode(mode);
    setSelectedFollowUp(followUp);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const showToast = (
    message: string,
    type: "success" | "error" | "info",
  ) => setToast({ message, type });

  const handleRangeChange = (next: DateRangeValue) => {
    setRange(next);
    const { from, to } = resolveDateRange(next);
    setFilters({ dueFrom: from, dueTo: to });
  };

  const handleClientSubmit = async (form: ClientFormValues) => {
    const { client, message } = await clientPicker.submitNewClient(form);
    if (client) {
      showToast("Client added successfully!", "success");
    } else {
      showToast(message || "Failed to add client", "error");
    }
  };

  const handleSubmit = async (form: FollowUpFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      if (modalMode === "create") {
        if (!organizationId) {
          showToast("No active organization", "error");
          return;
        }
        const payload: CreateFollowUpRequest = {
          organizationId,
          branchId: form.branchId || undefined,
          clientId: form.clientId,
          appointmentId: form.appointmentId || undefined,
          serviceId: form.serviceId || undefined,
          title: form.title || undefined,
          notes: form.notes || undefined,
          dueAt: new Date(form.dueAt).toISOString(),
          status: Number(form.status) as FollowUpStatus,
          priority: Number(form.priority),
          assignedTo: form.assignedTo || undefined,
          assignedName: form.assignedName || undefined,
        };
        const res = await createFollowUpAsync(token, payload);
        if (res.success) {
          closeModal();
          await reload();
          showToast("Follow-up added successfully!", "success");
        } else {
          showToast(res.message || "Failed to add follow-up", "error");
        }
        return;
      }

      if (modalMode === "edit" && selectedFollowUp) {
        const res = await updateFollowUpAsync(
          token,
          selectedFollowUp.id,
          toUpdateRequest(form),
        );
        if (res.success) {
          closeModal();
          await reload();
          showToast("Follow-up updated successfully!", "success");
        } else {
          showToast(res.message || "Failed to update follow-up", "error");
        }
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (
    followUp: FollowUp,
    status: FollowUpStatus,
  ) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await updateFollowUpAsync(token, followUp.id, { status });
      if (res.success) {
        await reload();
        showToast(`Marked as ${FOLLOWUP_STATUS_LABELS[status]}`, "success");
      } else {
        showToast(res.message || "Failed to update follow-up", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to update follow-up. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (followUp: FollowUp) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await deleteFollowUpAsync(token, followUp.id);
      if (res.success) {
        await reload();
        showToast("Follow-up deleted successfully!", "success");
      } else {
        showToast(res.message || "Failed to delete follow-up", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to delete follow-up. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="fu-empty">Loading follow-ups...</div>;
    }

    if (error) {
      return (
        <div className="fu-empty">
          <p>{error}</p>
          <button className="fu-btn fu-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    if (followUps.length === 0) {
      return (
        <div className="fu-empty">
          {filters.dueOnly
            ? "No follow-ups are due right now."
            : filters.search
              ? `No follow-ups match "${filters.search}".`
              : "No follow-ups found. Click \"Add Follow-Up\" to create one."}
        </div>
      );
    }

    return (
      <div className="fu-table-wrap">
        <table className="fu-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Title</th>
              <th>Due</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Assigned To</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {followUps.map((followUp) => {
              const clientName = followUp.clientName ?? "Unknown client";
              const overdue = isOverdue(followUp);
              return (
                <tr key={followUp.id}>
                  <td>
                    <div className="fu-name">
                      <span className="fu-avatar">
                        {(followUp.clientName ?? "?").charAt(0).toUpperCase()}
                      </span>
                      {clientName}
                    </div>
                  </td>
                  <td>
                    {followUp.title || (
                      <span className="fu-muted">—</span>
                    )}
                  </td>
                  <td>
                    <span className={overdue ? "fu-due fu-due--overdue" : "fu-due"}>
                      {formatDateTime(followUp.dueAt)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`fu-priority ${
                        followUp.priority === 3 ? "fu-priority--high" : ""
                      }`}
                    >
                      {PRIORITY_LABELS[followUp.priority] ?? followUp.priority}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`fu-badge ${FOLLOWUP_STATUS_CLASS[followUp.status]}`}
                    >
                      {FOLLOWUP_STATUS_LABELS[followUp.status]}
                    </span>
                  </td>
                  <td>
                    {staffMap.get(followUp.assignedTo ?? "")?.userName ??
                      followUp.assignedName ??
                      "Unassigned"}
                  </td>
                  <td className="fu-actions">
                    {canEditFollowUps && (
                      <button
                        className="fu-icon-btn fu-icon-btn--edit"
                        title="Edit"
                        aria-label={`Edit follow-up for ${clientName}`}
                        onClick={() => openModal("edit", followUp)}
                      >
                        <EditIcon />
                      </button>
                    )}
                    {canEditFollowUps &&
                      followUp.status === FollowUpStatus.Pending && (
                        <button
                          className="fu-icon-btn fu-icon-btn--contact"
                          title="Mark as contacted"
                          aria-label="Mark as contacted"
                          onClick={() =>
                            handleStatusChange(followUp, FollowUpStatus.Contacted)
                          }
                        >
                          <ContactIcon />
                        </button>
                      )}
                    {canEditFollowUps &&
                      (followUp.status === FollowUpStatus.Pending ||
                        followUp.status === FollowUpStatus.Contacted) && (
                        <button
                          className="fu-icon-btn fu-icon-btn--complete"
                          title="Mark as completed"
                          aria-label="Mark as completed"
                          onClick={() =>
                            handleStatusChange(followUp, FollowUpStatus.Completed)
                          }
                        >
                          <CompleteIcon />
                        </button>
                      )}
                    {canDeleteFollowUps && (
                      <button
                        className="fu-icon-btn fu-icon-btn--delete"
                        title="Delete"
                        aria-label={`Delete follow-up for ${clientName}`}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete follow-up for "${clientName}"?`,
                            )
                          ) {
                            handleDelete(followUp);
                          }
                        }}
                      >
                        <DeleteIcon />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const startPage = (pagination.page - 1) * pagination.pageSize + 1;
  const endPage = Math.min(
    pagination.page * pagination.pageSize,
    pagination.totalCount,
  );

  if (!entitlementsLoading && entitlements && !entitlements.hasActiveSubscription) {
    return (
      <div className="fu-page">
        <SubscriptionSectionGate section="follow-ups" />
      </div>
    );
  }

  return (
    <div className="fu-page">
      <div className="fu-header">
        <div>
          <h2>Follow-Ups</h2>
          <p>
            Track which clients need follow-up services for{" "}
            {user?.activeOrganization?.name ?? "your organization"}
          </p>
        </div>
        {canCreateFollowUps && (
          <button
            className="fu-btn fu-btn--primary"
            onClick={() => openModal("create", null)}
          >
            + Add Follow-Up
          </button>
        )}
      </div>

      <form
        className="fu-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters({ search: searchInput.trim() || undefined });
        }}
      >
        <label className="fu-filter fu-filter--grow">
          <span>Search</span>
          <input
            type="search"
            placeholder="Client name or phone…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </label>
        <button type="submit" className="fu-btn fu-btn--secondary">
          Search
        </button>
        <DateRangeFilter
          label="Due Date"
          value={range}
          disabled={filters.dueOnly}
          onChange={handleRangeChange}
        />
        <label className="fu-filter">
          <span>Status</span>
          <select
            value={
              filters.status === undefined ? "" : String(filters.status)
            }
            disabled={filters.dueOnly}
            onChange={(e) =>
              setFilters({
                status: e.target.value
                  ? (Number(e.target.value) as FollowUpStatus)
                  : undefined,
              })
            }
          >
            <option value="">All statuses</option>
            {Object.entries(FOLLOWUP_STATUS_LABELS).map(
              ([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ),
            )}
          </select>
        </label>
        <button
          type="button"
          className={`fu-btn ${
            filters.dueOnly ? "fu-btn--active" : "fu-btn--secondary"
          }`}
          onClick={() => setFilters({ dueOnly: !filters.dueOnly })}
        >
          Due Now
        </button>
        <button
          type="button"
          className="fu-btn fu-btn--secondary"
          onClick={() => {
            setSearchInput("");
            const next = emptyDateRangeValue();
            setRange(next);
            const { from, to } = resolveDateRange(next);
            setFilters({
              status: undefined,
              dueOnly: false,
              search: undefined,
              dueFrom: from,
              dueTo: to,
            });
          }}
        >
          Reset
        </button>
      </form>

      {renderBody()}

      {!loading &&
        !error &&
        !filters.dueOnly &&
        pagination.totalCount > 0 && (
          <div className="fu-pagination">
            <span className="fu-pagination__info">
              Showing {startPage}–{endPage} of {pagination.totalCount}
            </span>
            <div className="fu-pagination__controls">
              <button
                className="fu-btn fu-btn--secondary"
                disabled={!pagination.hasPrevious}
                onClick={() => setPage(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="fu-pagination__page">
                Page {pagination.page} of {Math.max(1, pagination.totalPages)}
              </span>
              <button
                className="fu-btn fu-btn--secondary"
                disabled={!pagination.hasNext}
                onClick={() => setPage(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <FollowUpModal
        isOpen={modalOpen}
        mode={modalMode}
        followUp={selectedFollowUp}
        branches={branches}
        services={services}
        staff={staff}
        submitting={submitting}
        lookupClient={clientPicker.lookupClient}
        appointmentClient={
          selectedFollowUp
            ? {
                fullName: selectedFollowUp.clientName,
                phone: selectedFollowUp.clientPhone,
                email: selectedFollowUp.clientEmail,
                address: selectedFollowUp.clientAddress,
                age: selectedFollowUp.clientAge,
              }
            : null
        }
        preselectedClient={clientPicker.createdClient}
        onNewClient={clientPicker.openNewClient}
        canCreateClient={canCreateClients}
        onClose={() => {
          closeModal();
          clientPicker.clearCreatedClient();
        }}
        onSubmit={handleSubmit}
      />

      <ClientModal
        isOpen={clientPicker.clientModalOpen}
        mode="create"
        client={null}
        submitting={clientPicker.submittingClient}
        initial={clientPicker.clientPrefill ?? undefined}
        onClose={clientPicker.closeNewClient}
        onSubmit={handleClientSubmit}
      />
    </div>
  );
};

export default FollowUpsPage;